import React, { useState, useEffect, useRef } from "react";
import mainStyle from "../../dashboard.module.scss";
import { useTheme } from "@mui/material/styles";
import { Box, MenuItem, Select, FormControl, Typography } from "@mui/material";
import * as echarts from "echarts";
import icn_linebar_chart from "assets/images/icons/icn_linebar_chart.svg";

import { mockHours, trafficDataset } from "../../const";

const TrafficStatistics = ({ sortable, dataset = trafficDataset }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [selectedCam, setSelectedCam] = useState("cam2");

  const hours = mockHours;

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    const chart = echarts.init(chartDom);
    chartInstance.current = chart;

    const currentData = dataset[selectedCam] || { passing: [], entering: [], leaving: [], entryRate: [] };

    const option = {
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        borderColor: "rgba(0, 0, 0, 0.08)",
        borderWidth: 1,
        shadowColor: "rgba(0, 0, 0, 0.08)",
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowOffsetY: 2,
        textStyle: { color: "#333333" },
        formatter: (params) => {
          if (!params || params.length === 0) return "";
          let text = `<div style="font-size: 13px; font-family: Inter, sans-serif; padding: 4px 8px; color: #333333;">`;
          text += `<div style="font-weight: bold; margin-bottom: 8px; color: #111111;">${params[0].name}</div>`;

          params.forEach((p) => {
            const val = p.seriesName === "Entry Rate" ? `${p.value}%` : p.value;
            let markerHtml = "";
            if (p.seriesName === "Entry Rate") {
              // Thin line
              markerHtml = `<span style="display:inline-block; margin-right:8px; width:16px; height:2px; background-color:${p.color}; vertical-align:middle;"></span>`;
            } else {
              // Thick rect
              markerHtml = `<span style="display:inline-block; margin-right:8px; width:16px; height:6px; background-color:${p.color}; vertical-align:middle; border-radius:1px;"></span>`;
            }

            text += `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; gap: 24px;">`;
            text += `<span style="display: flex; align-items: center; color: #555555;">${markerHtml}${p.seriesName}</span>`;
            text += `<span style="font-weight: bold; color: #111111;">${val}</span>`;
            text += `</div>`;
          });

          text += `</div>`;
          return text;
        },
      },
      legend: {
        bottom: 0,
        left: "5%",
        itemGap: 24,
        itemHeight: 8,
        textStyle: { color: theme.palette.primary.greyPrimary || "#666" },
        data: [
          { name: "Passing by", icon: "rect" },
          { name: "Entering", icon: "rect" },
          { name: "Leaving", icon: "rect" },
          { name: "Entry Rate", icon: "path://M0 100 H1000 V200 H0 Z" }
        ],
      },
      grid: {
        left: "3%",
        right: "3%",
        top: "15%",
        bottom: "45",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: hours,
        axisLabel: {
          color: theme.palette.primary.greyFourth || "#888",
          fontSize: 11,
        },
        axisLine: {
          lineStyle: { color: theme.palette.primary.greyThread || "#DDD" },
        },
      },
      yAxis: [
        {
          type: "value",
          name: "People",
          nameTextStyle: { color: "#888", fontSize: 10 },
          axisLabel: {
            color: theme.palette.primary.greyFourth || "#888",
            fontSize: 11,
          },
          splitLine: {
            lineStyle: {
              color: theme.palette.primary.greyThread || "#EEE",
              type: "dashed",
            },
          },
        },
        {
          type: "value",
          name: "Rate",
          nameTextStyle: { color: "#888", fontSize: 10 },
          min: 0,
          max: 100,
          axisLabel: {
            formatter: "{value}%",
            color: theme.palette.primary.greyFourth || "#888",
            fontSize: 11,
          },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: "Passing by",
          type: "bar",
          data: currentData.passing,
          itemStyle: { color: "#90CAF9" },
          barMaxWidth: 10,
        },
        {
          name: "Entering",
          type: "bar",
          data: currentData.entering,
          itemStyle: { color: "#81C784" },
          barMaxWidth: 10,
        },
        {
          name: "Leaving",
          type: "bar",
          data: currentData.leaving,
          itemStyle: { color: "#E0E0E0" },
          barMaxWidth: 10,
        },
        {
          name: "Entry Rate",
          type: "line",
          yAxisIndex: 1,
          data: currentData.entryRate,
          smooth: true,
          showSymbol: false,
          symbol: "none",
          lineStyle: { color: "#E57373", width: 2 },
          itemStyle: { color: "#E57373" },
        },
      ],
    };

    chart.setOption(option, true);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(chartDom);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [selectedCam, theme]);

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      className={`main-card-content ${mainStyle["dndObj_card"]} ${sortable === true ? mainStyle["dndObj_edit"] : ""
        }`}
    >
      <Box className={mainStyle["dndObj_title"]} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img src={icn_linebar_chart} alt="traffic_stats_icon" />
          <Typography component="span" sx={{ marginLeft: "10px" }}>Traffic Statistics</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Show</Typography>
          <FormControl size="small" variant="outlined">
            <Select
              value={selectedCam}
              onChange={(e) => setSelectedCam(e.target.value)}
              sx={{
                height: "28px",
                fontSize: "12px",
                borderRadius: "6px",
                "& .MuiSelect-select": { py: 0.5, px: 1 },
              }}
            >
              <MenuItem value="cam1" sx={{ fontSize: "12px" }}>Cam-01</MenuItem>
              <MenuItem value="cam2" sx={{ fontSize: "12px" }}>Cam-02</MenuItem>
              <MenuItem value="cam3" sx={{ fontSize: "12px" }}>Cam-03</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, position: "relative", px: 2, pb: 1 }}>
        <Box ref={chartRef} sx={{ width: "100%", height: "205px" }} />
        <Typography
          sx={{
            position: "absolute",
            bottom: "24px",
            right: "24px",
            fontSize: "12px",
            fontWeight: "normal",
            color: "text.secondary",
          }}
        >
          Last 24 hrs
        </Typography>
      </Box>
    </Box>
  );
};

export default TrafficStatistics;
